import { useMemo } from "react";
import { Box } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import { Content, OrderItem } from "@/types";
import {
  ColumnFilled,
  ColumnFree,
  DefaultFilled,
  DefaultFree,
  DefaultText,
  ListText,
  MediaFilled,
  PriceDownText,
  PriceUpText,
  SquareMiddlePrice,
} from "../preview-widget/components";
import Price from "../Price";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { openItem } from "@/store/uiSlice";
import { getOrderItemPrice } from "@/utils/order";
import { sanitizeNumber } from "@/utils/numbers";
import { useNavigate } from "react-router-dom";
import { getThumbnailImage } from "@/utils/media";
interface CarouselViewProps<I extends Content> {
  items: I[];
  previewWidget: string;
}

const CarouselView = <I extends Content>({ items = [], previewWidget = "media-filled" }: CarouselViewProps<I>) => {
  const activeOrganization = useAppSelector((store) => store.ui.activeOrganization);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const PreviewWidget = useMemo(() => {
    switch (previewWidget) {
      case "default-text":
        return DefaultText;
      case "list-text":
        return ListText;
      case "price-down-text":
        return PriceDownText;
      case "price-up-text":
        return PriceUpText;
      case "default-filled":
        return DefaultFilled;
      case "default-free":
        return DefaultFree;
      case "column-filled":
        return ColumnFilled;
      case "column-free":
        return ColumnFree;
      case "media-filled":
        return MediaFilled;
      case "square-middle-price":
        return SquareMiddlePrice;
      default:
        return null;
    }
  }, [previewWidget]);

  return (
    <Box sx={{ width: 700 }}>
      <Swiper
        spaceBetween={30}
        slidesPerView={3}
        modules={[Autoplay, Pagination]}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
        }}
      >
        {(items ?? []).map((item, index) => {
          return PreviewWidget ? (
            <SwiperSlide key={`${item.id}-${index}`}>
              <PreviewWidget
                title={item.title}
                description={item.previewDescription ? (item?.previewDescription as string) : item.description}
                price={<Price currency="NIO">{item.price != 0 ? getOrderItemPrice(item as unknown as OrderItem) : sanitizeNumber(item?.startingPrice)}</Price>}
                image={getThumbnailImage({ file: item?.images, organizationId: activeOrganization, size: '150x150' })}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(openItem(item));
                  navigate(`?itemId=${item.id}`);
                }}
              />
            </SwiperSlide>
          ) : null;
        })}
      </Swiper>
    </Box>
  );
};

export default CarouselView;
